// FWAA Business Exchange — company bucket detail.
// Who's attending from this company (with titles + claim buttons) and
// the full property book (Property / City / PM / Regional + Apollo).

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { ClaimControl, ClaimSummary, FwaaPropertyTable, RoleBadge } from '../components/FwaaShared';

export default function FwaaCompanyPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.getFwaaCompany(id)
      .then(d => { setData(d); setError(''); })
      .catch(err => setError(err?.responseBody?.error || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="directory-page fwaa-page"><p>Loading...</p></div>;
  if (error || !data) {
    return (
      <div className="directory-page fwaa-page">
        <Link to="/sales/fwaa" className="fwaa-back">← FWAA Business Exchange</Link>
        <div className="card" style={{ padding: 16, color: '#dc2626' }}>{error || 'Not found'}</div>
      </div>
    );
  }

  const { company, attendees, properties, claim_board } = data;

  return (
    <div className="directory-page fwaa-page">
      <Link to="/sales/fwaa" className="fwaa-back">← FWAA Business Exchange</Link>

      <div className="fwaa-detail-head">
        <div>
          <h1 className="fwaa-hero__title">{company.name}</h1>
          {company.aln_name && company.aln_name !== company.name ? (
            <p className="fwaa-hero__sub">ALN: {company.aln_name}</p>
          ) : null}
        </div>
        <ClaimSummary board={claim_board} />
      </div>

      <div className="fwaa-kpi-row">
        <HeadKpi value={company.attendee_count} label="Attendees Here" />
        <HeadKpi value={company.property_count} label="Properties" />
        <HeadKpi value={company.apollo_count} label="Apollo Accounts" accent="green" />
        <HeadKpi value={company.total_units ? company.total_units.toLocaleString('en-US') : '—'} label="Total Units" />
      </div>

      <h2 className="fwaa-section-title">Who&rsquo;s attending ({attendees.length})</h2>
      <div className="fwaa-attendee-grid">
        {attendees.map(a => <AttendeeCard key={a.id} attendee={a} myUserId={user?.id} onChanged={load} />)}
        {attendees.length === 0 ? <div className="fwaa-claimboard-empty">No attendees listed for this company.</div> : null}
      </div>

      <h2 className="fwaa-section-title">Property book ({properties.length})</h2>
      <p className="fwaa-section-sub">
        Rows marked <span className="fwaa-here-dot">●</span> have their regional at the exchange — click to open that contact.
      </p>
      <FwaaPropertyTable properties={properties} />
    </div>
  );
}

function AttendeeCard({ attendee, myUserId, onChanged }) {
  const hasBook = attendee.property_count > 0;
  const inner = (
    <>
      <div className="fwaa-attendee-card__top">
        <div>
          <div className="fwaa-attendee-card__name">{attendee.name}</div>
          <div className="fwaa-attendee-card__title">{attendee.title || '—'}</div>
        </div>
        <RoleBadge roleType={attendee.role_type} title={attendee.match_result} />
      </div>
      <div className="fwaa-attendee-card__foot">
        {hasBook ? (
          <span className="fwaa-attendee-card__stats">
            {attendee.property_count} {attendee.property_count === 1 ? 'property' : 'properties'}
            {attendee.apollo_count > 0 ? <span className="fwaa-attendee-card__apollo"> · {attendee.apollo_count} Apollo</span> : null}
          </span>
        ) : (
          <span className="fwaa-attendee-card__stats fwaa-muted">No mapped portfolio</span>
        )}
        <ClaimControl attendee={attendee} myUserId={myUserId} onChanged={onChanged} />
      </div>
    </>
  );

  if (hasBook) {
    return <Link to={`/sales/fwaa/attendees/${attendee.id}`} className="fwaa-attendee-card fwaa-attendee-card--link">{inner}</Link>;
  }
  return <div className="fwaa-attendee-card">{inner}</div>;
}

function HeadKpi({ value, label, accent }) {
  return (
    <div className={`fwaa-kpi${accent ? ` fwaa-kpi--${accent}` : ''}`}>
      <div className="fwaa-kpi__value">{value}</div>
      <div className="fwaa-kpi__label">{label}</div>
    </div>
  );
}
